import React, { useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  UserIcon,
  ShieldCheckIcon,
  CreditCardIcon,
  BellIcon,
  CodeBracketSquareIcon,
  SquaresPlusIcon,
  ArrowRightOnRectangleIcon,
  PencilIcon,
  EnvelopeIcon,
  ChevronRightIcon,
  QuestionMarkCircleIcon,
  ChatBubbleLeftEllipsisIcon,
  EyeIcon,
  EyeSlashIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';
import { CheckBadgeIcon, CubeIcon } from '@heroicons/react/24/solid';
import { useAuthStore } from '../../store/useAuthStore';
import { apiClient } from '../../lib/axios';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import logo from '../../assets/logo.png';

function pwStrength(pw: string): number {
  if (!pw) return 0;
  let s = 0;
  if (pw.length >= 8) s++;
  if (/[A-Z]/.test(pw)) s++;
  if (/[0-9]/.test(pw)) s++;
  if (/[^A-Za-z0-9]/.test(pw)) s++;
  return s;
}

const STRENGTH_LABEL = ['', 'Weak', 'Fair', 'Good', 'Strong'];
const STRENGTH_COLOR = ['', 'bg-red-500', 'bg-amber-400', 'bg-brand-400', 'bg-emerald-500'];

export default function UserProfile() {
  useDocumentTitle('Profile & Settings', 'Manage your Localcontact account profile, security, and preferences.');

  const navigate = useNavigate();
  const { user, setUser, clearAuth } = useAuthStore();

  const [name, setName] = useState(user?.name ?? '');
  const [email, setEmail] = useState(user?.email ?? '');
  const [avatarPreview, setAvatarPreview] = useState<string | null>(
    user?.avatar ? `${user.avatar}` : null
  );
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingAvatar, setSavingAvatar] = useState(false);
  const avatarInputRef = useRef<HTMLInputElement>(null);

  const [pwOpen, setPwOpen] = useState(false);
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [savingPw, setSavingPw] = useState(false);
  const [latestPayment, setLatestPayment] = useState<{ plan_name: string; expires_at: string | null } | null>(null);

  const strength = pwStrength(newPw);

  React.useEffect(() => {
    apiClient.get('/user/payments').then(res => {
      const payments: any[] = res.data;
      if (payments.length > 0) {
        const latest = payments[0];
        setLatestPayment({
          plan_name: latest.plan_name,
          expires_at: latest.expires_at,
        });
      }
    }).catch(() => {});
  }, []);

  const handleAvatarSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAvatarFile(file);
    setAvatarPreview(URL.createObjectURL(file));
  };

  const handleSaveAvatar = async () => {
    if (!avatarFile) return;
    setSavingAvatar(true);
    const formData = new FormData();
    formData.append('avatar', avatarFile);
    try {
      const { data } = await apiClient.post('/user/profile/avatar', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setAvatarPreview(data.avatar_url);
      setAvatarFile(null);
      if (user) setUser({ ...user, avatar: data.avatar_url });
      toast.success('Avatar updated.');
    } catch {
      toast.error('Failed to upload avatar.');
    } finally {
      setSavingAvatar(false);
    }
  };

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    try {
      const { data } = await apiClient.put('/user/profile', { name, email });
      if (user) setUser({ ...user, name: data.name, email: data.email });
      toast.success('Profile updated.');
    } catch (err: any) {
      const msg = err?.response?.data?.message ?? 'Failed to update profile.';
      toast.error(msg);
    } finally {
      setSavingProfile(false);
    }
  };

  const handleChangePassword = async () => {
    if (newPw !== confirmPw) { toast.error('Passwords do not match.'); return; }
    if (newPw.length < 8) { toast.error('Password must be at least 8 characters.'); return; }
    setSavingPw(true);
    try {
      await apiClient.post('/user/profile/password', {
        current_password: currentPw,
        password: newPw,
        password_confirmation: confirmPw,
      });
      setCurrentPw(''); setNewPw(''); setConfirmPw('');
      setPwOpen(false);
      toast.success('Password updated successfully.');
    } catch (err: any) {
      const msg = err?.response?.data?.message ?? 'Failed to update password.';
      toast.error(msg);
    } finally {
      setSavingPw(false);
    }
  };

  const handleLogout = async () => {
    try { await apiClient.post('/logout'); } catch {}
    clearAuth();
    navigate('/login');
  };

  const initials = (user?.name ?? 'U').split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();

  return (
    <div className="flex h-screen bg-slate-50 font-sans overflow-hidden">

      {/* Settings Sidebar */}
      <aside className="w-[280px] bg-white border-r border-slate-200 hidden md:flex flex-col justify-between shrink-0">
        <div className="flex flex-col h-full">
          <div className="h-24 flex items-center px-8 shrink-0">
            <img src={logo} alt="Localcontact" className="h-9 w-auto object-contain" />
          </div>

          <div className="px-6 pt-4">
            <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wide mb-4 pl-2">Workspace Control</p>
            <nav className="space-y-1.5">
              <button className="w-full flex items-center gap-3.5 px-4 py-3 rounded-xl text-[15px] font-semibold bg-brand-50 text-slate-800">
                <UserIcon className="w-[22px] h-[22px] shrink-0" />
                Profile Settings
              </button>
              <Link to="/dashboard/billing" className="w-full flex items-center gap-3.5 px-4 py-3 rounded-xl text-[15px] font-semibold text-slate-500 hover:bg-slate-50 hover:text-slate-900 transition-colors">
                <CreditCardIcon className="w-[22px] h-[22px] shrink-0" />
                Billing & Plans
              </Link>
              <Link to="/dashboard/api-access" className="w-full flex items-center gap-3.5 px-4 py-3 rounded-xl text-[15px] font-semibold text-slate-500 hover:bg-slate-50 hover:text-slate-900 transition-colors">
                <CodeBracketSquareIcon className="w-[22px] h-[22px] shrink-0" />
                API Access
              </Link>
              {/* <button className="w-full flex items-center gap-3.5 px-4 py-3 rounded-xl text-[15px] font-semibold text-slate-500 hover:bg-slate-50 hover:text-slate-900 transition-colors">
                <BellIcon className="w-[22px] h-[22px] shrink-0" />
                Notifications
              </button>
              <button className="w-full flex items-center gap-3.5 px-4 py-3 rounded-xl text-[15px] font-semibold text-slate-500 hover:bg-slate-50 hover:text-slate-900 transition-colors">
                <SquaresPlusIcon className="w-[22px] h-[22px] shrink-0" />
                Integrations
              </button> */}
            </nav>
          </div>
        </div>

        <div className="p-6 flex flex-col gap-4 shrink-0">
          <div className="bg-[#0f172a] rounded-2xl p-5 shadow-xl shadow-slate-900/10">
            <h3 className="text-white text-[12px] font-bold uppercase tracking-wide mb-2">Need More Credits?</h3>
            <p className="text-slate-300 text-[13px] leading-relaxed mb-4">Top up your balance and keep searching.</p>
            <Link to="/dashboard/billing" className="block w-full bg-white text-slate-900 py-2.5 rounded-xl text-sm font-bold text-center hover:bg-slate-100 transition-all">
              Purchase Credits
            </Link>
          </div>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3.5 px-3 py-2 text-[15px] font-bold text-red-500 hover:bg-red-50 rounded-xl transition-colors"
          >
            <ArrowRightOnRectangleIcon className="w-6 h-6 shrink-0" /> Sign Out
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col h-screen overflow-hidden">

        <header className="h-[72px] bg-white border-b border-slate-200 flex items-center justify-between px-10 shrink-0">
          <nav className="flex items-center gap-8 h-full">
            <Link to="/dashboard" className="text-[15px] font-semibold text-slate-500 hover:text-slate-900 transition-colors h-full flex items-center">Dashboard</Link>
            <Link to="/dashboard/search" className="text-[15px] font-semibold text-slate-500 hover:text-slate-900 transition-colors h-full flex items-center">Search</Link>
            <button className="text-[15px] font-bold text-brand-600 h-full flex items-center border-b-2 border-brand-500">Settings</button>
          </nav>
          <div className="flex items-center gap-4">
            <div className="w-9 h-9 rounded-full bg-brand-500 flex items-center justify-center overflow-hidden shrink-0">
              {avatarPreview
                ? <img src={avatarPreview} alt="avatar" className="w-full h-full object-cover" />
                : <span className="text-[12px] font-black text-slate-900">{initials}</span>
              }
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto bg-slate-50 px-10 py-10">
          <div className="max-w-[1100px] mx-auto">

            {/* Page Header */}
            <div className="flex flex-col md:flex-row md:items-start justify-between gap-6 mb-8">
              <div>
                <div className="flex items-center gap-2 text-[11px] font-bold text-slate-400 uppercase tracking-wide mb-3">
                  <span>Workspace</span>
                  <ChevronRightIcon className="w-3 h-3" />
                  <span className="text-slate-900">Settings</span>
                </div>
                <h1 className="text-[32px] font-extrabold text-slate-900 tracking-tight">Account Profile</h1>
                <p className="text-[15px] text-slate-500 mt-1 font-medium max-w-lg">
                  Update your profile details and manage account security.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">

              {/* Left Column */}
              <div className="xl:col-span-2 flex flex-col gap-8">

                {/* Identity Card */}
                <div className="bg-white border border-slate-200 rounded-2xl p-8 shadow-sm">
                  <div className="flex flex-col md:flex-row gap-8 mb-8">

                    {/* Avatar */}
                    <div className="flex flex-col items-start gap-3 shrink-0">
                      <div className="relative">
                        <div className="w-24 h-24 rounded-full border-4 border-white shadow-lg overflow-hidden bg-brand-100 flex items-center justify-center">
                          {avatarPreview
                            ? <img src={avatarPreview} alt="avatar" className="w-full h-full object-cover" />
                            : <span className="text-2xl font-black text-brand-700">{initials}</span>
                          }
                        </div>
                        <button
                          onClick={() => avatarInputRef.current?.click()}
                          className="absolute bottom-0 right-0 w-7 h-7 bg-brand-500 text-slate-900 rounded-full flex items-center justify-center border-2 border-white shadow hover:bg-brand-600 transition-colors"
                        >
                          <PencilIcon className="w-3.5 h-3.5" />
                        </button>
                        <input ref={avatarInputRef} type="file" accept="image/*" className="hidden" onChange={handleAvatarSelect} />
                      </div>
                      {avatarFile && (
                        <button
                          onClick={handleSaveAvatar}
                          disabled={savingAvatar}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-brand-500 text-slate-900 text-[12px] font-bold rounded-lg hover:bg-brand-600 transition-colors disabled:opacity-60"
                        >
                          <CheckIcon className="w-3.5 h-3.5" />
                          {savingAvatar ? 'Saving…' : 'Save Photo'}
                        </button>
                      )}
                    </div>

                    <div>
                      <div className="flex items-center gap-3 mb-2">
                        <h2 className="text-[22px] font-extrabold text-slate-900">Identity Details</h2>
                        <span className="px-2.5 py-1 bg-blue-50 text-blue-700 text-[10px] font-extrabold uppercase tracking-wide rounded-md border border-blue-100/50">Verified</span>
                      </div>
                      <p className="text-[14px] text-slate-500 font-medium leading-relaxed max-w-md">
                        Your profile details are used across the platform. Keep your information up to date.
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
                    <div>
                      <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-wide mb-2 ml-1">Full Name</label>
                      <input
                        type="text"
                        value={name}
                        onChange={e => setName(e.target.value)}
                        className="w-full bg-slate-50 border border-slate-200 text-slate-900 text-[15px] font-semibold px-4 py-3 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-300 transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-wide mb-2 ml-1">Email Address</label>
                      <div className="relative">
                        <EnvelopeIcon className="w-4 h-4 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2" />
                        <input
                          type="email"
                          value={email}
                          onChange={e => setEmail(e.target.value)}
                          className="w-full bg-slate-50 border border-slate-200 text-slate-900 text-[15px] font-semibold pl-11 pr-4 py-3 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-300 transition-all"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end">
                    <button
                      onClick={handleSaveProfile}
                      disabled={savingProfile}
                      className="bg-brand-500 text-slate-900 px-6 py-2.5 rounded-xl text-[14px] font-bold hover:bg-brand-600 transition-colors shadow-sm disabled:opacity-60"
                    >
                      {savingProfile ? 'Saving…' : 'Save Changes'}
                    </button>
                  </div>
                </div>

                {/* Security Card */}
                <div className="bg-white border border-slate-200 rounded-2xl p-8 shadow-sm">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-11 h-11 bg-slate-900 rounded-xl flex items-center justify-center shrink-0">
                      <ShieldCheckIcon className="w-5 h-5 text-white" />
                    </div>
                    <h3 className="text-[14px] font-extrabold text-slate-900 uppercase tracking-wide">Security</h3>
                  </div>

                  {/* Password row */}
                  <button
                    onClick={() => setPwOpen(v => !v)}
                    className="w-full flex items-center justify-between p-4 hover:bg-slate-50 rounded-xl cursor-pointer transition-colors border border-transparent hover:border-slate-100 mb-2"
                  >
                    <div className="text-left">
                      <h4 className="text-[14px] font-bold text-slate-900 mb-0.5">Change Password</h4>
                      <p className="text-[12px] text-slate-400 font-medium">Update your account password</p>
                    </div>
                    <ChevronRightIcon className={`w-5 h-5 text-slate-500 transition-transform ${pwOpen ? 'rotate-90' : ''}`} />
                  </button>

                  {pwOpen && (
                    <div className="border border-slate-100 rounded-xl p-5 space-y-4 bg-slate-50">
                      {/* Current password */}
                      <div>
                        <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-wide mb-2">Current Password</label>
                        <div className="relative">
                          <input
                            type={showCurrent ? 'text' : 'password'}
                            value={currentPw}
                            onChange={e => setCurrentPw(e.target.value)}
                            placeholder="••••••••"
                            className="w-full bg-white border border-slate-200 text-slate-900 text-[14px] px-4 pr-10 py-3 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-300 transition-all"
                          />
                          <button type="button" onClick={() => setShowCurrent(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                            {showCurrent ? <EyeSlashIcon className="w-4 h-4" /> : <EyeIcon className="w-4 h-4" />}
                          </button>
                        </div>
                      </div>
                      {/* New password */}
                      <div>
                        <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-wide mb-2">New Password</label>
                        <div className="relative">
                          <input
                            type={showNew ? 'text' : 'password'}
                            value={newPw}
                            onChange={e => setNewPw(e.target.value)}
                            placeholder="••••••••"
                            className="w-full bg-white border border-slate-200 text-slate-900 text-[14px] px-4 pr-10 py-3 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-300 transition-all"
                          />
                          <button type="button" onClick={() => setShowNew(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                            {showNew ? <EyeSlashIcon className="w-4 h-4" /> : <EyeIcon className="w-4 h-4" />}
                          </button>
                        </div>
                        {newPw && (
                          <div className="mt-2 flex items-center gap-2">
                            <div className="flex gap-1 flex-1">
                              {[1, 2, 3, 4].map(i => (
                                <div key={i} className={`h-1.5 flex-1 rounded-full transition-colors ${i <= strength ? STRENGTH_COLOR[strength] : 'bg-slate-200'}`} />
                              ))}
                            </div>
                            <span className={`text-[11px] font-bold ${strength <= 1 ? 'text-red-500' : strength === 2 ? 'text-amber-500' : strength === 3 ? 'text-brand-500' : 'text-emerald-500'}`}>
                              {STRENGTH_LABEL[strength]}
                            </span>
                          </div>
                        )}
                      </div>
                      {/* Confirm */}
                      <div>
                        <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-wide mb-2">Confirm New Password</label>
                        <div className="relative">
                          <input
                            type={showConfirm ? 'text' : 'password'}
                            value={confirmPw}
                            onChange={e => setConfirmPw(e.target.value)}
                            placeholder="••••••••"
                            className="w-full bg-white border border-slate-200 text-slate-900 text-[14px] px-4 pr-10 py-3 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-300 transition-all"
                          />
                          <button type="button" onClick={() => setShowConfirm(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                            {showConfirm ? <EyeSlashIcon className="w-4 h-4" /> : <EyeIcon className="w-4 h-4" />}
                          </button>
                        </div>
                      </div>
                      <div className="flex justify-end gap-3 pt-1">
                        <button onClick={() => { setPwOpen(false); setCurrentPw(''); setNewPw(''); setConfirmPw(''); }} className="px-4 py-2.5 text-[13px] font-semibold text-slate-500 hover:text-slate-800 transition-colors">
                          Cancel
                        </button>
                        <button
                          onClick={handleChangePassword}
                          disabled={savingPw || !currentPw || !newPw || !confirmPw}
                          className="bg-brand-500 text-slate-900 px-5 py-2.5 rounded-xl text-[13px] font-bold hover:bg-brand-600 transition-colors disabled:opacity-60"
                        >
                          {savingPw ? 'Updating…' : 'Update Password'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>

              </div>

              {/* Right Column */}
              <div className="xl:col-span-1 flex flex-col gap-8">

                {/* Plan card */}
                <div className="bg-gradient-to-b from-brand-600 to-brand-800 rounded-3xl p-8 text-white shadow-xl shadow-brand-900/20 relative overflow-hidden">
                  <div className="absolute top-0 right-0 -mr-10 -mt-10 w-40 h-40 bg-white/10 blur-3xl rounded-full pointer-events-none" />
                  <div className="flex items-start justify-between mb-8 relative z-10">
                    <div className="w-11 h-11 rounded-2xl bg-white/10 border border-white/20 flex items-center justify-center backdrop-blur-md">
                      <CubeIcon className="w-5 h-5 text-white" />
                    </div>
                    {latestPayment && new Date(latestPayment.expires_at ?? 0) >= new Date() ? (
                      <span className="px-3 py-1.5 bg-white/10 border border-white/20 rounded-full text-[10px] font-extrabold tracking-wide uppercase backdrop-blur-md">Active</span>
                    ) : (
                      <span className="px-3 py-1.5 bg-red-500/30 border border-red-300/20 rounded-full text-[10px] font-extrabold tracking-wide uppercase backdrop-blur-md">Expired</span>
                    )}
                  </div>
                  <div className="relative z-10 mb-6">
                    <div className="flex items-center gap-2 mb-1">
                      <h2 className="text-[28px] font-extrabold tracking-tight">{latestPayment?.plan_name ?? 'Free'}</h2>
                      <CheckBadgeIcon className="w-5 h-5 text-emerald-400" />
                    </div>
                    {latestPayment?.expires_at ? (
                      <p className={`text-[13px] font-medium ${
                        new Date(latestPayment.expires_at) < new Date()
                          ? 'text-red-200'
                          : 'text-white opacity-90'
                      }`}>
                        {new Date(latestPayment.expires_at) < new Date()
                          ? 'Expired'
                          : `Expires ${new Date(latestPayment.expires_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
                        }
                      </p>
                    ) : (
                      <p className="text-white text-[13px] font-medium opacity-90">No active plan</p>
                    )}
                  </div>
                  <div className="bg-white/10 border border-white/10 rounded-2xl p-5 mb-6 relative z-10">
                    <p className="text-[10px] font-extrabold text-white uppercase tracking-wide mb-1">Credits Left</p>
                    <div className="text-[32px] font-extrabold tracking-tight mb-3 text-white">{(user?.credits ?? 0).toLocaleString()}</div>
                    <div className="w-full h-1.5 bg-white/10 rounded-full overflow-hidden">
                      <div className="w-[45%] h-full bg-white rounded-full" />
                    </div>
                  </div>
                  <div className="flex flex-col gap-2.5 relative z-10">
                    <Link to="/dashboard/billing" className="block w-full bg-white text-brand-900 py-3 rounded-xl text-[14px] font-bold text-center hover:bg-slate-50 transition-colors">
                      Purchase Credits
                    </Link>
                    <Link to="/dashboard/billing" className="block w-full bg-white/10 border border-white/20 text-white py-3 rounded-xl text-[14px] font-bold text-center hover:bg-white/20 transition-colors">
                      Manage Subscription
                    </Link>
                  </div>
                </div>

                {/* Help card */}
                <div className="bg-white border border-slate-200 rounded-2xl p-7 shadow-sm">
                  <h3 className="text-[11px] font-bold text-slate-400 uppercase tracking-wide mb-5">Need Assistance?</h3>
                  <div className="flex flex-col gap-5">
                    <div className="flex items-start gap-4 cursor-pointer group">
                      <div className="w-9 h-9 rounded-full bg-slate-50 border border-slate-100 flex items-center justify-center shrink-0 group-hover:bg-brand-50 transition-colors">
                        <QuestionMarkCircleIcon className="w-5 h-5 text-slate-400 group-hover:text-brand-500 transition-colors" />
                      </div>
                      <div>
                        <h4 className="text-[14px] font-bold text-slate-900 mb-0.5 group-hover:text-brand-600 transition-colors">Help Center</h4>
                        <p className="text-[12px] text-slate-400 font-medium">Documentation & Guides</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4 cursor-pointer group">
                      <div className="w-9 h-9 rounded-full bg-slate-50 border border-slate-100 flex items-center justify-center shrink-0 group-hover:bg-brand-50 transition-colors">
                        <ChatBubbleLeftEllipsisIcon className="w-5 h-5 text-slate-400 group-hover:text-brand-500 transition-colors" />
                      </div>
                      <div>
                        <h4 className="text-[14px] font-bold text-slate-900 mb-0.5 group-hover:text-brand-600 transition-colors">Priority Support</h4>
                        <p className="text-[12px] text-slate-400 font-medium">24/7 Human Support</p>
                      </div>
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
